import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { AppStore } from '~/stores/app';
import api from '~/utils/api';
import { OrderEditType } from '~/utils/constants';
import { makeCrewAction, makeCrewLoader, redirect } from '~/utils/routing';
import { addressFormSchema, orderEditActionSchema } from '~/utils/schemas';
import { AsyncReturnType } from '~/utils/types';

export const editActionTypeFormKey = 'editType';

// TODO: enforce all keys
const formSchema = zfd.formData(
  z.discriminatedUnion(editActionTypeFormKey, [
    z.object(
      {
        [editActionTypeFormKey]: z.literal(
          OrderEditType.UPDATE_SHIPPING_ADDRESS,
        ),
        address: addressFormSchema,
      },
      {
        message:
          'Something is not quite right with the shipping address. Please check your address and try again.',
      },
    ),

    z.object({
      [editActionTypeFormKey]: z.literal(OrderEditType.CANCEL_ORDER),
    }),
  ]),
);

const extractBody = (
  data: z.infer<typeof formSchema>,
  editAction: z.infer<typeof orderEditActionSchema> | null,
) =>
  match({ data, currentAction: editAction })
    .with(
      {
        data: { editType: 'UPDATE_SHIPPING_ADDRESS' },
        currentAction: { editType: 'UPDATE_SHIPPING_ADDRESS' },
      },
      ({ data: { address, editType }, currentAction }) => ({
        editType,
        action: currentAction,
        stagedEdits: {
          updatedShippingAddress: {
            ...address,
            stateOrProvinceCode: address.stateOrProvinceCode ?? undefined,
            phone: address.phone ?? undefined,
          },
        },
      }),
    )
    .with(
      {
        data: { editType: 'CANCEL_ORDER' },
        currentAction: { editType: 'CANCEL_ORDER' },
      },
      ({ data: { editType }, currentAction }) => ({
        editType,
        action: currentAction,
      }),
    )
    .otherwise(() => {
      throw new Error('Invalid edit action');
    });

const handleStageResponse =
  (appStore: AppStore) =>
  (stagedEdit: AsyncReturnType<typeof api.stageOrderEdit>) => {
    appStore.dispatch(appStore.actions.orderEdit.assignEdits(stagedEdit));

    return redirect(`../commit`);
  };

export const { action, useActionResult } = makeCrewAction(
  async ({ context, formData }, appStore) => {
    const { orderEdit } = appStore.getState();

    return (
      match(formSchema.safeParse(formData))
        // TODO: verify redirects / clear state if necessary
        .with({ success: false }, ({ error }) => ({
          ok: false,
          message: error.message,
        }))
        .with({ success: true }, ({ data }) =>
          api
            .stageOrderEdit({
              params: {
                storefrontId: context.settings.storefrontId,
                storeOrderId: context.order.id,
              },
              body: extractBody(data, orderEdit.action),
            })
            .then(handleStageResponse(appStore)),
        )
        .exhaustive()
    );
  },
);

export const { loader, useLoaderData } = makeCrewLoader(
  async ({ context }, appStore) =>
    match(appStore.getState().orderEdit)
      .with({ step: P.not(P.union('editing', 'reviewing')) }, () =>
        redirect('../'),
      )
      .with({ editType: 'UPDATE_SHIPPING_ADDRESS' }, (state) => ({
        editType: state.action.editType,
        shippingAddress: context.order.shippingAddress,
        action: state.action,
        headline: 'Update Shipping Address',
      }))
      // Add to the union for types that don't require staging
      .with({ editType: P.union('CANCEL_ORDER') }, (state) =>
        api
          .stageOrderEdit({
            params: {
              storefrontId: context.settings.storefrontId,
              storeOrderId: context.order.id,
            },
            body: {
              editType: state.action.editType,
              action: state.action,
            },
          })
          .then(handleStageResponse(appStore)),
      )
      .exhaustive(),
);
